import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
  LogLevel,
} from '@azure/msal-browser';
import { AuthService, ClientConfiguration } from '../types/AuthTypes';

/**
 * B2CAuthService
 * 
 * Handles Azure AD B2C authentication with:
 * - Redirect-based flows (no popups)
 * - localStorage for multi-tab session sync
 * - Standard user authentication
 */
export class B2CAuthService implements AuthService {
  private msalInstance: PublicClientApplication | null = null;
  private config: ClientConfiguration | null = null;

  async initialize(config: ClientConfiguration): Promise<void> {
    this.config = config;

    // Helper to parse string arrays like "['a', 'b']" to ['a', 'b']
    const parseStringArray = (str: string): string[] => {
      if (Array.isArray(str)) return str;
      if (typeof str === 'string') {
        try {
          // Remove surrounding quotes and split
          const cleaned = str.replace(/^['"]|['"]$/g, '').replace(/^\[|\]$/g, '');
          return cleaned.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        } catch {
          return [];
        }
      }
      return [];
    };

    const scopes = parseStringArray(config.scopes);
    const b2cAuthority = `${config.customDomain}/${config.tenantName}/${config.b2cPolicy || 'B2C_1A_Signup_Signin'}`;
    const msalConfig: Configuration = {
      auth: {
        clientId: config.clientId,
        authority: b2cAuthority,
        knownAuthorities: [config.customDomain],
        redirectUri: config.redirectUrl,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: 'localStorage', // Multi-tab sync
        storeAuthStateInCookie: false,
      },
      system: {
        allowNativeBroker: false,
        allowRedirectInIframe: false, // Prevent accidental iframe usage
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            if (containsPii) return;
            
            switch (level) {
              case LogLevel.Error:
                console.error('[B2CAuth]', message);
                break;
              case LogLevel.Warning:
                console.warn('[B2CAuth]', message);
                break;
              case LogLevel.Info:
                console.info('[B2CAuth]', message);
                break;
              case LogLevel.Verbose:
                console.debug('[B2CAuth]', message);
                break;
            }
          },
        },
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
  }

  /**
   * Initiate B2C login via redirect with interaction state checking
   */
  async login(): Promise<void> {
    if (!this.msalInstance || !this.config) {
      throw new Error('B2CAuthService not initialized');
    }

    try {
      // Check if there are any active accounts that might indicate an ongoing process
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.info('[B2CAuthService] Active account found, checking if login needed');
        // If we have accounts but no active one, might be in transition
        const activeAccount = this.msalInstance.getActiveAccount();
        if (!activeAccount) {
          console.warn('[B2CAuthService] Accounts exist but none active, clearing stale state');
          await this.msalInstance.clearCache();
        }
      }

      // Helper to parse string arrays like "['a', 'b']" to ['a', 'b']
      const parseStringArray = (str: string): string[] => {
        if (Array.isArray(str)) return str;
        if (typeof str === 'string') {
          try {
            // Remove surrounding quotes and split
            const cleaned = str.replace(/^['"]|['"]$/g, '').replace(/^\[|\]$/g, '');
            return cleaned.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
          } catch {
            return [];
          }
        }
        return [];
      };

      const scopes = parseStringArray(this.config.scopes);
      const apiScope = scopes[scopes.length - 1] || 'openid'; // Use last scope as apiScope

      console.info('[B2CAuthService] Initiating login redirect');
      await this.msalInstance.loginRedirect({
        scopes: ['openid', 'profile', 'offline_access', apiScope],
      });
    } catch (error: any) {
      console.error('[B2CAuthService] Login failed:', error);
      
      // If login fails due to interaction in progress, try clearing state
      if (error.message?.includes('interaction') || 
          error.message?.includes('in progress') ||
          error.errorCode === 'interaction_in_progress') {
        console.warn('[B2CAuthService] Interaction in progress error during login, clearing cache and retrying');
        try {
          await this.msalInstance.clearCache();
          // Don't retry immediately to avoid loops
          throw new Error('Authentication state was cleared. Please try logging in again.');
        } catch (clearError) {
          console.error('[B2CAuthService] Failed to clear cache after login error:', clearError);
        }
      }
      
      throw error;
    }
  }

  /**
   * Handle redirect response after B2C login
   */
  async handleRedirect(): Promise<AuthenticationResult | null> {
    if (!this.msalInstance) {
      throw new Error('B2CAuthService not initialized');
    }

    try {
      // Always call handleRedirectPromise to process any redirect response
      console.info('[B2CAuthService] Processing redirect response...');
      const response = await this.msalInstance.handleRedirectPromise();
      
      if (response?.account) {
        console.info('[B2CAuthService] Successful redirect response received');
        
        // Clean up URL after successful authentication
        this.cleanupRedirectUrl();
        
        return response;
      } else {
        console.info('[B2CAuthService] No redirect response or account in response');
        return null;
      }
    } catch (error: any) {
      console.error('[B2CAuthService] Error handling redirect:', error);
      
      // Clean up URL even on error to prevent stuck state
      this.cleanupRedirectUrl();
      
      throw error;
    }
  }

  /**
   * Clean up redirect parameters from URL
   */
  private cleanupRedirectUrl(): void {
    try {
      const url = new URL(window.location.href);
      const hasRedirectParams = url.searchParams.has('code') || 
                               url.searchParams.has('error') || 
                               url.searchParams.has('state') ||
                               url.hash.includes('code=') || 
                               url.hash.includes('error=');
      
      if (hasRedirectParams) {
        // Remove redirect parameters
        url.searchParams.delete('code');
        url.searchParams.delete('error');
        url.searchParams.delete('state');
        url.searchParams.delete('session_state');
        url.hash = '';
        
        // Update URL without reload
        window.history.replaceState(null, '', url.toString());
        console.info('[B2CAuthService] Cleaned up redirect parameters from URL');
      }
    } catch (error) {
      console.warn('[B2CAuthService] Failed to cleanup redirect URL:', error);
    }
  }

  /**
   * Acquire token silently for API calls
   */
  async acquireToken(account: AccountInfo): Promise<string> {
    if (!this.msalInstance || !this.config) {
      throw new Error('B2CAuthService not initialized');
    }

    // Helper to parse string arrays like "['a', 'b']" to ['a', 'b']
    const parseStringArray = (str: string): string[] => {
      if (Array.isArray(str)) return str;
      if (typeof str === 'string') {
        try {
          // Remove surrounding quotes and split
          const cleaned = str.replace(/^['"]|['"]$/g, '').replace(/^\[|\]$/g, '');
          return cleaned.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        } catch {
          return [];
        }
      }
      return [];
    };

    const scopes = parseStringArray(this.config.scopes);
    const apiScope = scopes[scopes.length - 1] || 'openid'; // Use last scope as apiScope

    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'offline_access', apiScope],
        account,
      });
      
      return result.accessToken;
    } catch (error: any) {
      // Handle authority mismatch - this can happen when config changes between sessions
      if (error.errorCode === 'authority_mismatch') {
        console.warn('Authority mismatch detected, clearing cache and redirecting to login');
        // Clear all cached accounts and tokens
        await this.msalInstance.clearCache();
        throw new Error('AUTHORITY_MISMATCH');
      }
      throw error;
    }
  }

  /**
   * Logout via redirect with interaction state checking
   */
  async logout(): Promise<void> {
    if (!this.msalInstance || !this.config) {
      throw new Error('B2CAuthService not initialized');
    }

    try {
      console.info('[B2CAuthService] Initiating logout redirect');
      await this.msalInstance.logoutRedirect({
        postLogoutRedirectUri: this.config.redirectUrl,
      });
    } catch (error: any) {
      console.error('[B2CAuthService] Logout failed:', error);
      
      // If logout fails due to interaction in progress, try a more aggressive approach
      if (error.message?.includes('interaction') || 
          error.message?.includes('in progress') ||
          error.errorCode === 'interaction_in_progress') {
        console.warn('[B2CAuthService] Interaction in progress error detected, clearing cache and redirecting manually');
        
        try {
          // Clear all cached data
          await this.msalInstance.clearCache();
          
          // Manual redirect to logout URL
          const logoutUrl = `${this.config.customDomain}/${this.config.tenantName}/${this.config.b2cPolicy}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(this.config.redirectUrl)}`;
          window.location.href = logoutUrl;
        } catch (fallbackError) {
          console.error('[B2CAuthService] Fallback logout also failed:', fallbackError);
          // Last resort: just clear local cache and redirect to home
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = this.config.redirectUrl;
        }
      } else {
        throw error;
      }
    }
  }

  /**
   * Get all cached accounts
   */
  getAllAccounts(): AccountInfo[] {
    if (!this.msalInstance) {
      return [];
    }
    return this.msalInstance.getAllAccounts();
  }

  /**
   * Set the active account
   */
  setActiveAccount(account: AccountInfo): void {
    if (!this.msalInstance) {
      throw new Error('B2CAuthService not initialized');
    }
    this.msalInstance.setActiveAccount(account);
  }

  /**
   * Get the MSAL instance (for advanced use cases)
   */
  getInstance(): PublicClientApplication | null {
    return this.msalInstance;
  }
}
